import { useState, useEffect } from 'react';
import { X, Percent } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

// Predefined factoring companies list
const PREDEFINED_COMPANIES = [
  'RTS Financial Service Inc.',
  'Zermatt Capital LLC',
  'Compass Funding Solutions',
  'eCapital Freight Factoring Corp.',
  'Engaged Financial LLC',
  'TRU Funding',
  'Capital Depot',
  'Sunbelt Finance',
  'J D Factors',
  'Pathward',
  'OTR Solutions',
  'Triumph',
  'Pro Funding',
  'WEX Capital',
];

interface FactoringCompanyFormData {
  company_id: string;
  name: string;
  automatic_document_transfer: boolean;
  contact_person: string;
  email: string;
  phone: string;
  contact_phone_ext: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  load_factoring_fee_percentage: string;
  notes: string;
}

interface FactoringCompanyFormProps {
  companyId?: string;
  onClose: () => void;
  onSuccess: () => void;
  isModal?: boolean;
}

export function FactoringCompanyForm({ companyId, onClose, onSuccess, isModal = true }: FactoringCompanyFormProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!companyId);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const [formData, setFormData] = useState<FactoringCompanyFormData>({
    company_id: `FC-${Date.now()}`,
    name: '',
    automatic_document_transfer: false,
    contact_person: '',
    email: '',
    phone: '',
    contact_phone_ext: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    load_factoring_fee_percentage: '',
    notes: '',
  });

  useEffect(() => {
    if (companyId) {
      loadCompany();
    }
  }, [companyId]);

  async function loadCompany() {
    try {
      const { data, error } = await supabase
        .from('factoring_companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      
      if (data) {
        setFormData({
          company_id: data.company_id || '',
          name: data.name || '',
          automatic_document_transfer: data.automatic_document_transfer ?? false,
          contact_person: data.contact_person || '',
          email: data.email || '',
          phone: data.phone || '',
          contact_phone_ext: data.contact_phone_ext || '',
          street: data.street || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          load_factoring_fee_percentage: data.load_factoring_fee_percentage?.toString() || '',
          notes: data.notes || '',
        });
      }
    } catch (error) {
      console.error('Error loading company:', error);
      toast.error('Failed to load factoring company');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      const dataToSave = {
        ...formData,
        load_factoring_fee_percentage: formData.load_factoring_fee_percentage 
          ? parseFloat(formData.load_factoring_fee_percentage) 
          : null,
      };

      if (companyId) {
        // Update existing company
        const { error } = await supabase
          .from('factoring_companies')
          .update({
            ...dataToSave,
            updated_at: new Date().toISOString(),
          })
          .eq('id', companyId);

        if (error) throw error;
        toast.success('Factoring company updated successfully!');
      } else {
        // Create new company
        const { error } = await supabase
          .from('factoring_companies')
          .insert([dataToSave]);

        if (error) throw error;
        toast.success('Factoring company added successfully!');
      }
      
      onSuccess();
    } catch (error: any) {
      console.error('Error saving company:', error);
      toast.error(error.message || 'Failed to save factoring company');
    } finally {
      setSaving(false);
    }
  };

  const selectPredefinedCompany = (companyName: string) => {
    setFormData({ ...formData, name: companyName });
    setShowDropdown(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Predefined Companies Dropdown */}
      {!companyId && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Select Factoring Company</h3>
          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Factoring Company *
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                onFocus={() => setShowDropdown(true)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Select or type company name..."
                required
              />
              {showDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {PREDEFINED_COMPANIES.filter(company => 
                    company.toLowerCase().includes(formData.name.toLowerCase())
                  ).map((company) => (
                    <button
                      key={company}
                      type="button"
                      onClick={() => selectPredefinedCompany(company)}
                      className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors text-sm"
                    >
                      {company}
                    </button>
                  ))}
                  {PREDEFINED_COMPANIES.filter(company => 
                    company.toLowerCase().includes(formData.name.toLowerCase())
                  ).length === 0 && (
                    <div className="px-4 py-2 text-sm text-slate-500">
                      No matches found. You can type a custom name.
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Select from the list or enter a custom name
            </p>
          </div>
        </div>
      )}

      {/* Company Name (for edit mode) */}
      {companyId && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Company Information</h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>
      )}

      {/* Automatic Document Transfer */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="automatic_document_transfer"
            checked={formData.automatic_document_transfer}
            onChange={(e) => setFormData({ ...formData, automatic_document_transfer: e.target.checked })}
            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="automatic_document_transfer" className="text-sm font-medium text-slate-700">
            Automatic Document Transfer
          </label>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Contact Name
            </label>
            <input
              type="text"
              value={formData.contact_person}
              onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Phone *
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <input
                type="text"
                placeholder="ext"
                value={formData.contact_phone_ext}
                onChange={(e) => setFormData({ ...formData, contact_phone_ext: e.target.value })}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Address</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Street
            </label>
            <input
              type="text"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                City *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                State *
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Zip Code *
              </label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Load Factoring Fee */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-900 border-b pb-2 flex-1">Load Factoring Fee</h3>
          <div className="text-xs text-slate-500 bg-blue-50 px-3 py-1 rounded-full">
            Fee categorized as expense in General Ledger
          </div>
        </div>
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Load Factoring Fee (%) *
          </label>
          <div className="relative">
            <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.load_factoring_fee_percentage}
              onChange={(e) => setFormData({ ...formData, load_factoring_fee_percentage: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 3.5"
              required
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Enter the percentage fee (e.g., 3.5 for 3.5%)
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : companyId ? 'Update Company' : 'Add Company'}
        </button>
      </div>
    </form>
  );

  if (!isModal) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div>
            <h2 className="text-2xl font-bold">
              {companyId ? 'Edit Factoring Company' : 'Add Factoring Company'}
            </h2>
            <p className="text-sm text-blue-100 mt-1">
              {companyId ? 'Update company information' : 'Enter company information'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {formContent}
      </div>
    </div>
  );
}
